import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, eachWeekOfInterval, eachMonthOfInterval, eachYearOfInterval, eachDayOfInterval } from "date-fns";
import _ from "lodash";
import { GetKpiVisitTypesQuery } from "~/graphql/__generated__/graphql";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type DateRange = "daily" | "weekly" | "monthly" | "yearly";

interface GroupedBarChartProps {
  data: GetKpiVisitTypesQuery["kpiVisitTypes"]["visitVolumeByDate"];
  chartTitle?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  colors: string[];
  startDate: Date;
  endDate: Date;
  dateRange: DateRange;
}

const GroupedBarChart: React.FC<GroupedBarChartProps> = ({
  data,
  chartTitle = "Category Breakdown",
  xAxisLabel,
  yAxisLabel = "Count",
  colors,
  startDate,
  endDate,
  dateRange,
}) => {
  const groupByDateRange = (date: Date) => {
    switch (dateRange) {
      case "weekly":
        return `Week ${format(startOfWeek(date), "MMM dd")} - ${format(endOfWeek(date), "MMM dd")}`;
      case "monthly":
        return format(startOfMonth(date), "MMM yyyy");
      case "yearly":
        return format(date, "yyyy");
      default:
        return format(date, "EEE dd");
    }
  };

  const interval = (() => {
    switch (dateRange) {
      case "weekly":
        return eachWeekOfInterval({ start: startDate, end: endDate }).map(start => ({
          start,
          label: groupByDateRange(start),
        }));
      case "monthly":
        return eachMonthOfInterval({ start: startDate, end: endDate }).map(start => ({
          start,
          label: groupByDateRange(start),
        }));
      case "yearly":
        return eachYearOfInterval({ start: startDate, end: endDate }).map(start => ({
          start,
          label: groupByDateRange(start),
        }));
      default:
        return eachDayOfInterval({ start: startDate, end: endDate }).map(start => ({
          start,
          label: groupByDateRange(start),
        }));
    }
  })();

  const groupedByDateRange = _.groupBy(data, (item) => item ? groupByDateRange(parseISO(item.date)) : null);
  const categories = _.uniq(data.map((item) => item?.category));

  const datasets = categories.map((category, index) => ({
    label: category,
    data: interval.map(({ label }) => {
      const total = _.sumBy(groupedByDateRange[label], (item) => (item?.category === category ? item?.count ?? 0 : 0));
      return total > 0 ? total : null;
    }),
    backgroundColor: colors[index % colors.length],
  }));

  const processedData = {
    labels: interval.map(({ label }) => label),
    datasets,
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
        text: chartTitle,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: yAxisLabel,
        },
      },
      x: {
        title: {
          display: true,
          text: xAxisLabel,
        },
      },
    },
  };

  return (
    <Bar
      data={{
        labels: processedData.labels,
        datasets: processedData.datasets,
      }}
      options={options}
    />
  );
};

export default GroupedBarChart;
